import { useState, useEffect, useCallback } from 'react';
import { useAuthContext } from '@/features/auth/context/AuthContext';
import { teamService } from '@/features/team/services/teamService/teamService';
import { customerService } from '@/features/card/services/customerService';
import type { DashboardSummary, UserSpendingSummary, UserDetails, CustomerDetails } from '@/features/dashboard/types/dashboard.types';
import { useAsyncGuard } from '@chikara/hooks';
import { useSpinner } from '@chikara/ui';
import { formatDayMonthYear } from '@chikara/utils';

import { t } from '@/i18n';

interface UseDashboardSummaryReturn {
  summary: DashboardSummary | null;
  userSpending: UserSpendingSummary | null;
  userDetails: UserDetails | null;
  customerDetails: CustomerDetails | null;
  error: string | null;
  refetch: (member_uuid: string) => Promise<void>;
}

export function useDashboardSummary(member_uuid: string): UseDashboardSummaryReturn {
  const { show, hide } = useSpinner();
  const { setMember, setPayload, setUserData } = useAuthContext();
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [userSpending, setUserSpending] = useState<UserSpendingSummary | null>(null);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [customerDetails, setCustomerDetails] = useState<CustomerDetails | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { checkGuard, resetGuard } = useAsyncGuard();

  const fetchSummary = useCallback(async (uuid: string) => {
    if (!uuid || !checkGuard()) {
      return;
    }

    setError(null);
    show();

    try {
      // Use existing member from context if it matches to avoid redundant API call
      // We check against member_uuid directly. If we need to check currentMember, 
      // we can do it inside without making it a dependency of the callback's identity.
      const userData = await teamService.getMember(uuid);

      let customerData = null;
      if (userData.customer_uuid) {
        customerData = await customerService.getCustomerSummary();
      }

      // Populate global state
      setMember(userData);
      setPayload(userData.uuid, userData.customer_uuid);
      setUserData(
        userData.email ?? '',
        `${userData.name ?? ''} ${userData.lname ?? ''}`.trim(),
        userData.role
      );


      const summaryResult = {
        creditLine: customerData?.credit_line.total_limit || 0,
        spent: customerData?.credit_line.total_spent || 0,
        disposable: customerData?.credit_line.total_available || 0,
        toPay: customerData?.payment_info.estimated_payment || 0,
        paymentDate: formatDayMonthYear(customerData?.payment_info.deadline || ''),
      };

      const accounts = (userData.account?.accounts || []).filter((accountItem: any) => {
        const card = accountItem.card;
        return card && Object.keys(card).length > 0 && card.status === 'enabled';
      });
      const balanceDue = accounts.reduce((acc: number, accountItem: any) => acc + (accountItem.balance?.balance_due || 0), 0);
      const blocked = accounts.reduce((acc: number, accountItem: any) => acc + (accountItem.balance?.blocked || 0), 0);
      const monthlyLimit = accounts.reduce((acc: number, accountItem: any) => acc + (accountItem.balance?.limit_assigned || 0), 0);

      const userSpending = {
        limit: monthlyLimit,
        spent: balanceDue + blocked,
      };

      const userDetails = {
        name: userData.name,
        role: userData.role,
        status: userData.status,
      }

      const customerDetails = {
        legal_name: userData.customer?.general.legal_name,
        key_id: customerData?.customer_id || "N/A",
      }

      setSummary(summaryResult);
      setUserSpending(userSpending);
      setUserDetails(userDetails);
      setCustomerDetails(customerDetails);
    } catch {
      setError(t('dashboard.errors.generic'));
      resetGuard();
    } finally {
      hide();
    }
  }, [setMember, setPayload, setUserData, show, hide, checkGuard, resetGuard]);

  useEffect(() => {
    if (member_uuid) {
      fetchSummary(member_uuid);
    }
  }, [fetchSummary, member_uuid]);

  return {
    summary,
    userSpending,
    userDetails,
    customerDetails,
    error,
    refetch: (uuid: string) => {
      resetGuard();
      return fetchSummary(uuid);
    }
  };
}
